<script lang="ts">
    import {afterUpdate, beforeUpdate} from 'svelte';
    import type {ValueEditorSize} from '../../types';
    import {onDestroy} from 'svelte';
    import type {FormControl} from '../../internal/forms/form-control';
    import {FormControlBuilder} from '../../internal/forms/form-control';
    import {validatorFactory, addPrefixAndSuffix, trimPrefixAndSuffix} from './utils';
    import {formControls} from '../../internal/forms/use.form-controls';
    import {style} from 'svelte-forms';
    import ErrorMessages from '../../errors/ErrorMessages.svelte';
    import {focusOnMount} from '../../internal/use.focus-on-mount';
    import type {TextValueEditorLocalizations, TextValueEditorOptions, TextValueEditorValidations} from './types';
    import Description from '../../description/Description.svelte';
    import {aceEditor} from './use.ace-editor';
    import {touched} from '../../internal/use.touched';
    import {ariaInvalid} from '../../errors/use.aria-invalid';
    import {ariaErrormessage} from '../../errors/use.aria-errormessage';
    import {ariaDescribedby} from '../../description/use.aria-describedby';
    import {getContext} from 'svelte';
    import {createInputDescription} from '../../description/utils';
    import {EDITORS_LOCALIZE} from '../../context-keys';
    import type {FormControlOptions} from '../../internal/forms/types';
    import {emptyAsNullFormatter, emptyAsNullParser, trim} from '../_shared/editors-utils';
    import type {EditorsLocalizationFunction} from '../../localizations/types';
    import {getValueEditorLabelContext} from '../../label/value-editor-label-context';
    import {ariaLabelledby} from '../../label/use.aria-labelledby';
    import {required} from '../../internal/forms/use.required';
    import {getLogger} from '../../../../svelte/context';
    import {disabledAttribute} from '../../internal/use.disabled-attribute';

    export let model: string;
    export let formControlOptions: FormControlOptions;

    export let editorId: string;
    export let editorName: string;
    export let placeholder: string;

    export let isDisabled: boolean;
    export let isFocused: boolean;
    export let forceShowErrors: boolean;
    export let size: ValueEditorSize;

    export let options: TextValueEditorOptions;
    export let validations: TextValueEditorValidations;

    const originalType = options.type;
    const TEXT_INPUTS = [
        'text',
        'email',
        'url',
        'tel'
    ];

    const localize: EditorsLocalizationFunction<TextValueEditorLocalizations> = getContext(EDITORS_LOCALIZE);
    const labelContext = getValueEditorLabelContext();

    const formControl = FormControlBuilder.for(editorId, editorName, model)
        .withValidators(validatorFactory(validations, options.type))
        .withOptions(formControlOptions)
        .withParsers(isPrefixOrSuffixType() ? [addPrefixAndSuffix(options)] : [])
        .withModifiers([trim(options), emptyAsNullParser(options)])
        .withFormatters([...isPrefixOrSuffixType() ? [trimPrefixAndSuffix(options)] : [], emptyAsNullFormatter(options)])
        .build(getLogger());

    const fieldValue$ = formControl.getFieldStateAsStore();

    const subscription = formControl.getModelValue$().subscribe((value) => model = value);  // two-way data binding -> sending updated value up to a parent
    $: formControl.setModelValue(model); // two-way data binding -> receiving new model value from parent

    let inputElement: HTMLElement;
    let inputElementHasFocus: boolean;

    const inputDescription = createInputDescription({editorId, editorName, description: localize('description')});

    let aceEditorOptions;
    $: aceEditorOptions = {...options.aceOptions, placeholder, readOnly: isDisabled, isFocused};

    beforeUpdate(() => {
        inputElementHasFocus = document.activeElement === inputElement;
    });

    afterUpdate(() => {
        if (inputElementHasFocus && document.activeElement !== inputElement) {
            inputElement.focus();
        }
    });

    onDestroy(() => {
        subscription.unsubscribe();
    })

    function isText(): boolean {
        return TEXT_INPUTS.includes(options.type);
    }

    export function getFormController(): FormControl<string> {
        return formControl;
    }

    function countRowsInString(str: string = ''): number {
        const rowsCount = ((str.match(/\n/g) || []).length) + 1;

        if (rowsCount === 1) {
            return Math.floor(str.length / 60) + 1;
        }

        return rowsCount;
    }

    function getNumberOfRows(value: string, minRows: number = 1, maxRows: number = 10): number {
        if (value) {
            return  Math.min(Math.max(countRowsInString(value), minRows), maxRows);
        }
        return minRows;
    }

    function processThreshold(viewValue: string) {
        if (options.switchToTextareaThreshold !== 0 &&
            originalType === 'text' &&
            typeof viewValue === 'string' &&
            viewValue.length >= options.switchToTextareaThreshold) {

            if (options.type !== 'textarea') {
                options.type = 'textarea';
            }
        } else if (options.type !== originalType) {
            options.type = originalType;
        }
    }

    function isPrefixOrSuffixType() {
        return isText() && (options.prefix || options.suffix);
    }

    $: processThreshold($fieldValue$.value);

</script>

<template>
    {#if isPrefixOrSuffixType()}
        <div class="input-group input-group-{size}" role="group">
            {#if options.prefix}
                <div class="input-group-addon prefix">{options.prefix}</div>
            {/if}
            <input
                    class="form-control input-{size}"

                    type={options.type}
                    id={editorId}
                    name={editorName}
                    {placeholder}
                    disabled={isDisabled}
                    maxlength={validations.maxlength}
                    minlength={validations.minlength}
                    use:required={validations.required}
                    use:focusOnMount={isFocused}

                    use:formControls={formControl}
                    use:touched={{formControl}}
                    use:style={{field: formControl.getFieldStateAsStore()}}
                    use:ariaInvalid={formControl}
                    use:ariaErrormessage={formControl}
                    use:ariaDescribedby={inputDescription}
                    bind:this={inputElement}

                    data-main-input/>

            {#if options.suffix}
                <div class="input-group-addon suffix">{options.suffix}</div>
            {/if}
        </div>
    {/if}

    {#if isText() && !(options.prefix || options.suffix)}
        <input
                class="form-control input-{size}"

                type={options.type}
                id={editorId}
                name={editorName}
                {placeholder}
                disabled={isDisabled}
                maxlength={validations.maxlength}
                minlength={validations.minlength}
                use:required={validations.required}
                use:focusOnMount={isFocused}

                use:formControls={formControl}
                use:touched={{formControl}}
                use:style={{field: formControl.getFieldStateAsStore()}}
                use:ariaInvalid={formControl}
                use:ariaErrormessage={formControl}
                use:ariaDescribedby={inputDescription}
                bind:this={inputElement}

                data-main-input/>
    {/if}

    {#if options.type === 'textarea'}
        <textarea
                class="form-control input-{size}"

                id={editorId}
                name={editorName}
                {placeholder}
                disabled={isDisabled}
                maxlength={validations.maxlength}
                minlength={validations.minlength}
                use:required={validations.required}
                use:focusOnMount={isFocused}
                use:touched={{formControl}}
                rows={getNumberOfRows($fieldValue$.value)}

                use:formControls={formControl}
                use:style={{field: formControl.getFieldStateAsStore()}}
                use:ariaInvalid={formControl}
                use:ariaErrormessage={formControl}
                use:ariaDescribedby={inputDescription}
                bind:this={inputElement}

                data-main-input>
        </textarea>

    {/if}

    {#if options.type === 'rich-textarea'}
        <div class="form-control ace-editor"
             id={editorId}
             data-name={editorName}

             use:disabledAttribute={isDisabled}
             use:aceEditor={{formControl, options: aceEditorOptions}}
             use:touched={{formControl, touchTriggerEvent: 'ace-blur'}}
             use:style={{field: formControl.getFieldStateAsStore()}}
             use:ariaInvalid={formControl}
             use:ariaErrormessage={formControl}
             use:ariaDescribedby={inputDescription}
             use:ariaLabelledby={labelContext}
             bind:this={inputElement}

             data-main-input>
        </div>
    {/if}

    <ErrorMessages formController={formControl} {forceShowErrors} {size}/>

    <Description {inputDescription}/>
</template>

<style lang="less">
    .ace-editor {
        min-height: 300px;
    }

    textarea {
        resize: vertical;
    }

    textarea.input-xs {
        padding: 2px 5px;
        font-size: 12px;
        line-height: 1.5;
        border-radius: 3px;
        height: auto;
    }
</style>